import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { toCents } from '@threadheads/shared';
import { NoteLine } from '@/components/shared/NoteLine';
import { ChartCard } from './charts/ChartPrimitives';
import { formatPercent } from '../lib/reporting-format';
import { partLabel, type UsageSegment } from '../lib/three-pl-usage-view';
import { INLINE_ACTION_CLASS, Swatch } from './ThreePlCells';

interface ThreePlUsageTotalProps {
  /** The panel heading, already translated ("To bill, 1 Oct to 8 Oct"). */
  title: string;
  scope: 'all' | 'one';
  /** Money to bill in the scope. */
  amount: number;
  /** The response's currency code ("USD"), printed beside the amount: `$` alone is ambiguous. */
  unit: string;
  /** Money parts of `amount`, by client (All clients) or by bill section (one bill). */
  segments: UsageSegment[];
  /** Formats in the report's currency (D2), never the viewer's. */
  formatValue: (value: number) => string;
  /** Pieces shipped, orders and pieces on the 3PL's stock: the All clients summary line. */
  pieces: number;
  ownerStock: number;
  orders: number;
  ownerName: string;
  /**
   * Lines with no price on the bill(s) in scope (owner `skus.costs_manage`
   * holders only, D28);
   * null for everyone else. Above 0 the amount is not final, and the panel
   * says so under it (C3).
   */
  noPrice: number | null;
  /** Scrolls to and focuses the first line with no price. Keep it stable: the panel is memoised. */
  onShowNoPrice: () => void;
}

/**
 * Total panel: the money to bill in the period, a split bar and its legend.
 * By client on All clients (the 3 largest bills plus Other past 4 clients),
 * by bill section for one bill (five parts, always in the bill's order). The
 * light segments are under 3:1 on white, so every amount they stand for is
 * also printed as text in the legend.
 */
function ThreePlUsageTotalImpl({
  title,
  scope,
  amount,
  unit,
  segments,
  formatValue,
  pieces,
  ownerStock,
  orders,
  ownerName,
  noPrice,
  onShowNoPrice,
}: ThreePlUsageTotalProps) {
  const { t } = useTranslation('inventory');
  const notFinalId = React.useId();
  const separator = t('reporting.threePlUsage.listSeparator');
  const labelOf = (segment: UsageSegment) =>
    partLabel(segment, (key) => t(key, { owner: ownerName }));
  const barLabel = t('reporting.threePlUsage.total.barAriaMoney', {
    parts: segments.map((segment) => `${labelOf(segment)}: ${formatValue(segment.value)}`).join(separator),
  });
  const drawn = segments.filter((segment) => toCents(segment.value) > 0);
  const totalCents = toCents(amount);

  return (
    <ChartCard title={title}>
      <div className="space-y-4">
        <div className="space-y-1">
          <p className="flex items-baseline gap-2">
            <span className="text-3xl font-bold tabular-nums tracking-tight">{formatValue(amount)}</span>
            <span className="text-sm text-muted-foreground">{unit}</span>
          </p>
          {/* Owner `skus.costs_manage` holders only: the amount above is not
              final while a line has no price. Same count and unit as the clients
              table and the bill's warnings (lines); Show jumps to the first one. */}
          {noPrice !== null && noPrice > 0 && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <NoteLine tone="warning" icon={AlertTriangle} id={notFinalId}>
                {t('reporting.threePlUsage.total.notFinal', { count: noPrice })}
              </NoteLine>
              <button
                type="button"
                onClick={onShowNoPrice}
                aria-describedby={notFinalId}
                className={INLINE_ACTION_CLASS}
              >
                {t('reporting.threePlUsage.total.showNoPrice')}
              </button>
            </div>
          )}
        </div>

        <div
          role="img"
          aria-label={barLabel}
          className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full bg-muted"
        >
          {drawn.map((segment) => (
            <span
              key={segment.id}
              className="h-full"
              style={{
                flexGrow: toCents(segment.value),
                flexBasis: 0,
                minWidth: 4,
                backgroundColor: segment.colour,
              }}
            />
          ))}
        </div>

        {/* A long client or 3PL name wraps (never truncates: the full name is
            the only place this part is named). The swatch, the amount and the
            percent stay on the label's first line. */}
        <ul className="space-y-1.5">
          {segments.map((segment) => {
            const cents = toCents(segment.value);
            return (
              <li key={segment.id} className="flex items-start gap-2 text-sm leading-5">
                <span className="flex h-5 shrink-0 items-center">
                  <Swatch colour={segment.colour} />
                </span>
                <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{labelOf(segment)}</span>
                {cents === 0 ? (
                  <span className="whitespace-nowrap tabular-nums text-muted-foreground">-</span>
                ) : (
                  <span className="whitespace-nowrap font-medium tabular-nums">
                    {formatValue(segment.value)}
                  </span>
                )}
                <span className="w-10 shrink-0 whitespace-nowrap text-right text-xs leading-5 tabular-nums text-muted-foreground">
                  {cents === 0 || totalCents === 0 ? '' : formatPercent(cents / totalCents)}
                </span>
              </li>
            );
          })}
        </ul>

        {scope === 'all' && (
          <p className="text-xs text-muted-foreground">
            {t('reporting.threePlUsage.total.allSummary', {
              pieces: t('reporting.threePlUsage.count.pieces', { count: pieces }),
              orders: t('reporting.threePlUsage.count.orders', { count: orders }),
              ownerStock,
              owner: ownerName,
            })}
          </p>
        )}
      </div>
    </ChartCard>
  );
}

export const ThreePlUsageTotal = React.memo(ThreePlUsageTotalImpl);
